import {
  FastField,
  Field,
  FieldInputProps,
  FieldMetaProps,
  FieldProps
} from "formik";
import _ from "lodash";

import { FormHelperText, TextFieldProps } from "@material-ui/core";
import { Autocomplete, AutocompleteProps } from "@material-ui/lab";

import { useAutocompleteStyles, useFormInputStyles } from "src/styles/forms";

import FormTextField from "./FormTextField";

export type ComboboxProps = Omit<
  AutocompleteProps<
    { label: string; value: string },
    false /* multiple */,
    false /* disableClearable */,
    false /* freeSolo */
  >,
  "renderInput" | "multiple" | "value"
> & {
  label: string;
  value: string;
  textFieldProps?: TextFieldProps;
  error?: boolean;
};

export default function Combobox({
  value,
  options,
  error,
  textFieldProps,
  label,
  ...autocompleteProps
}: ComboboxProps): JSX.Element {
  const formInputStyles = useFormInputStyles();
  const autocompleteStyles = useAutocompleteStyles();

  return (
    <Autocomplete
      value={options.find(opt => opt.value === value)}
      getOptionSelected={(option, value) => option.value === value.value}
      getOptionLabel={option => option.label}
      options={options}
      renderOption={option => option.label}
      renderInput={params => (
        // @ts-ignore disableUnderline isn't technically allowed if you pass variant="outlined"
        <FormTextField
          {...params}
          {...(textFieldProps || {})}
          error={error}
          label={label}
          variant="outlined"
          InputLabelProps={{
            classes: {
              root: `${formInputStyles.inputLabel} ${autocompleteStyles.inputLabel}`
            }
          }}
        />
      )}
      classes={{
        inputRoot: `${formInputStyles.input} ${autocompleteStyles.inputRoot}`
      }}
      {...autocompleteProps}
    />
  );
}

export function FormikCombobox({
  name,
  fast,
  overrideFormikProps,
  ...comboboxProps
}: Omit<ComboboxProps, "value" | "onChange"> & {
  name: string;
  fast?: boolean;
  overrideFormikProps?: (
    field: FieldInputProps<string>,
    meta: FieldMetaProps<string>
  ) => FieldInputProps<string>;
}): JSX.Element {
  const Component = fast ? FastField : Field;
  return (
    <Component name={name}>
      {({ field: formikField, meta, form }: FieldProps) => {
        const formikProps = overrideFormikProps
          ? overrideFormikProps(formikField, meta)
          : formikField;
        const error = meta.touched ? meta.error : null;
        return (
          <>
            <Combobox
              {...comboboxProps}
              {...formikProps}
              error={!!error}
              onChange={(evt, val) => {
                form.setFieldValue(name, val?.value || "");
              }}
            />
            {error ? <FormHelperText error>{error}</FormHelperText> : null}
          </>
        );
      }}
    </Component>
  );
}
